import { PlusOutlined } from "@ant-design/icons";
import { Button, Modal, Dropdown, Menu, Tooltip } from "antd";
import HeaderPage from "components/headers/HeaderPage";
import { FilterIcon } from "components/svgs/FilterIcon";
import { canCreatePermission } from "configs/constants";
import { allowPermission } from "helper/util";
import { selectRole } from "modules/auth/store/authSlice";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import paginationFunc from "../../../../helper/hooks/pagination";
import InternalDriverFormHook from "../components/forms/InternalDriverFormHook";
import FilterInternalDriver from "../components/FilterInternalDriver";
import { ReactSVG } from "react-svg";
import { buildFirstLevelFilter } from "../helper";
import {
  selectAllInternalDrivers,
  selectFilter,
  selectMetadata,
  updateFilterDrivers,
  updatePaginationDrivers
} from "../store/slice";
import {
  doCreateInternalDriver,
  doDeleteInternalDriver,
  doUpdateInternalDriver,
  getInternalDriverList
} from "../store/thunks";
import InternalDriverTable from "../tables/InternalDriverTable";
import "./InternalDriverListPage.scss";
import { Link } from "react-router-dom/cjs/react-router-dom";
import { ProfileContext } from "layouts/private/ProfileContext";

const InternalDriverListPage = () => {
  const [state, setState] = useState({
    visibleModal: false,
    action: "",
    driver: null
  });
  const [showFilter, setShowFilter] = useState(false);
  const dispatch = useDispatch();

  const canCreate = allowPermission(useSelector(selectRole), canCreatePermission);
  const driverList = useSelector(selectAllInternalDrivers);
  const metadata = useSelector(selectMetadata);
  const filter = useSelector(selectFilter);
  const profileCtx = useContext(ProfileContext);
  const profile = useMemo(() => profileCtx.profile, [profileCtx]);
  const fetchProfile = useCallback(() => profileCtx.fetchProfile, [profileCtx]);
  const page = metadata?.currentPage || 1;
  const pageSize = metadata?.itemsPerPage || 10;
  const pagination = paginationFunc(
    metadata,
    "Drivers",
    updatePaginationDrivers,
    dispatch
  );

  const items = [
    {
      key: "item-1",
      label: (
        <a href={window.REACT_APP_DOWNLOAD_ANDROID_URL} target="_blank" rel="noreferrer">
          Android
        </a>
      )
    },
    {
      key: "item-2",
      label: (
        <a href={window.REACT_APP_DOWNLOAD_IOS_URL} target="_blank" rel="noreferrer">
          iOS
        </a>
      )
    }
  ];

  useEffect(() => {
    dispatch(
      getInternalDriverList({
        page,
        pageSize,
        filter: buildFirstLevelFilter({ ...filter, is_internal_driver: true })
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, page, pageSize, filter]);

  const handleClickFilter = (values) => {
    dispatch(updateFilterDrivers(values));
  };

  const handleDeleteInternalDriver = (id) => {
    dispatch(doDeleteInternalDriver(id));
  };

  const handleSubmit = (values) => {
    if (values.id) {
      dispatch(
        doUpdateInternalDriver({
          ...values
        })
      );
      fetchProfile();
    } else {
      dispatch(
        doCreateInternalDriver({
          ...values
        })
      );
      fetchProfile();
    }

    setState({
      ...state,
      visibleModal: false
    });
  };

  const handleClickEdit = (driver) => {
    setState({
      ...state,
      driver,
      visibleModal: true
    });
  };
  const filterButton = (
    <Button
      size={showFilter ? "normal" : "small"}
      className={`svg-icon ${showFilter && "filter"} d-flex flex-row`}
      onClick={() => setShowFilter(!showFilter)}
    >
      Filter <FilterIcon />
    </Button>
  );

  return (
    <div className="list-driver-page">
      <HeaderPage
        leftSide={<div className="title-header">DRIVERS</div>}
        rightSide={
          canCreate ? (
            <div className="d-flex gap-2">
              {filterButton}
              <Tooltip
                title={
                  profile?.subscription && !profile?.subscription?.areSeatsAvailable
                    ? "Please upgrade your subscription if you want to create more drivers."
                    : ""
                }
                placement="top"
              >
                <Button
                  type="secondary"
                  onClick={() =>
                    setState({
                      ...state,
                      visibleModal: true,
                      driver: null
                    })
                  }
                  disabled={
                    profile?.subscription && !profile?.subscription?.areSeatsAvailable
                  }
                >
                  Create Driver
                  <PlusOutlined />
                </Button>
              </Tooltip>
              <Dropdown trigger={"hover"} placement={"bottom"} menu={{ items }}>
                <div className={"driver-app"}>
                  <div className="first-chacracter clickable">
                    <Button size={"small"}>
                      Driver APP
                      <ReactSVG className="driver-icon" src="/icons/local_shipping.svg" />
                    </Button>
                  </div>
                </div>
              </Dropdown>
            </div>
          ) : (
            <div className="d-flex gap-2">
              {filterButton}
              <Dropdown trigger={"hover"} placement={"bottom"} menu={{ items }}>
                <div className={"driver-app"}>
                  <div className="first-chacracter clickable">
                    <Button size={"small"}>
                      Driver APP
                      <ReactSVG className="driver-icon" src="/icons/local_shipping.svg" />
                    </Button>
                  </div>
                </div>
              </Dropdown>
            </div>
          )
        }
      />
      {showFilter && (
        <FilterInternalDriver
          defaultValues={filter}
          setShowFilter={setShowFilter}
          handleClickFilter={handleClickFilter}
          onReset={() => dispatch(updateFilterDrivers(undefined))}
        />
      )}
      {profile?.subscription && !profile?.subscription?.areSeatsAvailable && (
        <div className="p-1" style={{ color: "#cf1322" }}>
          You have reached the internal driver limit for your subscription (
          {profile?.subscription?.internalDriversCount || 0} of{" "}
          {profile?.subscription?.totalSeats || 0} seats used). Please upgrade your
          subscription in the{" "}
          <Link to="/billing/upgrades">
            <span style={{ textDecoration: "underline", textUnderlineOffset: 4 }}>
              billing settings.
            </span>
          </Link>{" "}
        </div>
      )}
      <div className="content-table">
        <InternalDriverTable
          doDeleteInternalDriver={handleDeleteInternalDriver}
          dataSource={driverList}
          handleClickEdit={handleClickEdit}
          pagination={{ ...pagination, size: "small" }}
        />
      </div>
      {state.visibleModal && (
        <Modal
          closable={false}
          visible={true}
          title={"Create Driver"}
          className="no-padding-modal"
          onCancel={() =>
            setState({
              ...state,
              visibleModal: false
            })
          }
          footer={false}
          width={792}
        >
          <InternalDriverFormHook
            driver={state.driver}
            submitForm={handleSubmit}
            onCancel={() =>
              setState({
                ...state,
                visibleModal: false
              })
            }
          />
        </Modal>
      )}
    </div>
  );
};
export default InternalDriverListPage;
